import { useNavigate } from 'react-router-dom'

import { Button } from 'components/Button'
import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { EMPTY_VALUE } from 'constants/index'
import { routes } from 'constants/routes'
import { commonStrings, accountManagementStrings } from 'constants/strings'
import {
  EOrganizationMemberRole,
  EOrganizationMemberStatus,
  type TAdminAccount,
} from 'types'
import { getAccountOrganizationLabel } from './accountOrganizationDisplay'
import type { TTermsMemberConsentState } from 'types/terms'
import { TermsStateBadge } from './TermsStateBadge'

type AccountsTableProps = {
  accounts: TAdminAccount[]
  loading: boolean
  emptyMessage?: string
  termsStateByAccountId?: Record<number, TTermsMemberConsentState>
  canEdit?: boolean
}

const buildAccountEditPath = (accountId: number) =>
  routes.ACCOUNT_MANAGEMENT_EDIT.replace(':accountId', String(accountId))

const roleLabelMap = {
  [EOrganizationMemberRole.ADMIN]: 'ADMIN',
  [EOrganizationMemberRole.STANDARD]: 'STANDARD',
} as const

const statusLabelMap = {
  [EOrganizationMemberStatus.ACTIVE]: 'ACTIVE',
  [EOrganizationMemberStatus.PAUSED]: 'PAUSED',
} as const

export function AccountsTable({
  accounts,
  loading,
  emptyMessage = accountManagementStrings.states.empty,
  termsStateByAccountId = {},
  canEdit = true,
}: AccountsTableProps) {
  const navigate = useNavigate()
  const handleEdit = (accountId: number) =>
    navigate(buildAccountEditPath(accountId))

  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = <DataTableMessageRow colSpan={8} message={commonStrings.loading} />
  } else if (accounts.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={8}
        message={emptyMessage}
      />
    )
  } else {
    body = accounts.map((account) => (
      <tr key={account.id} className="border-t border-border/60 bg-white">
        <td className="px-2 py-3 text-left sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.05)]">
          {canEdit ? (
            <Button
              type="button"
              size="sm"
              onClick={() => handleEdit(account.id)}
            >
              {commonStrings.actions.edit}
            </Button>
          ) : (
            <span className="text-[11px] text-muted">-</span>
          )}
        </td>
        <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
          {account.id}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {getAccountOrganizationLabel(account) || EMPTY_VALUE}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {account.name || EMPTY_VALUE}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {roleLabelMap[account.role]}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {account.email}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          {statusLabelMap[account.status]}
        </td>
        <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
          <TermsStateBadge state={termsStateByAccountId[account.id]} />
        </td>
      </tr>
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1000px]"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight break-words sticky left-0 z-10 bg-surface/70">
            {accountManagementStrings.table.headers.actions}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.id}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.organization}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.name}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.role}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.email}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.status}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {accountManagementStrings.table.headers.terms}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}
